import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Tab } from './Layout';
import './Dashboard.css';

export function Dashboard({ onNavigate }: { onNavigate: (tab: Tab) => void }): JSX.Element {
  return (
    <div className="dash-root">
      <div className="dash-scroll">
        <header className="dash-header">
          <h1 className="dash-title">Overview</h1>
          <p className="dash-subtitle">Inventory, auto removals, and monitoring at a glance</p>
        </header>

        <section className="dash-monitoring-section">
          <InventoryWidget onNavigate={onNavigate} />
        </section>

        <section className="dash-monitoring-section">
          <AutoRemovalWidget onNavigate={onNavigate} />
        </section>

        <section className="dash-monitoring-section">
          <MonitoringWidget onNavigate={onNavigate} />
        </section>
      </div>
    </div>
  );
}

interface InventoryKindSummary {
  count: number;
  capacity: number;
  free: number | null;
}

interface InventoryDashboardSummary {
  serken: InventoryKindSummary;
  regular: InventoryKindSummary;
}

interface MicrosoftSnapshot {
  admin_panels: number | null;
  capacity: number | null;
  free: number | null;
  computed_at: string | null;
  updated_at: string | null;
}

const INVENTORY_BUCKETS: ReadonlyArray<{
  kind: keyof InventoryDashboardSummary;
  label: string;
  accent: 'coral' | 'sage';
}> = [
  { kind: 'serken', label: 'Serken', accent: 'coral' },
  { kind: 'regular', label: 'Regular', accent: 'sage' },
];

const fmtNum = (n: number | null | undefined): string =>
  n === null || n === undefined ? '-' : n.toLocaleString('en-US');

function InventoryWidget({ onNavigate }: { onNavigate: (tab: Tab) => void }): JSX.Element {
  const [summary, setSummary] = useState<InventoryDashboardSummary | null>(null);
  const [ms, setMs] = useState<MicrosoftSnapshot | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullMsg, setPullMsg] = useState<string | null>(null);

  useEffect(() => {
    api<InventoryDashboardSummary>('/api/inventory-panels/dashboard-summary')
      .then(setSummary)
      .catch(() => {
        /* keep last good state */
      })
      .finally(() => setLoaded(true));
    // Microsoft is an instant DB snapshot (not a live count); load alongside.
    api<MicrosoftSnapshot>('/api/inventory-panels/microsoft-summary')
      .then(setMs)
      .catch(() => {
        /* keep last good state */
      });
  }, []);

  const goToPage = () => onNavigate('inventory-pull');
  // Microsoft cards are an external link; stop the section's internal nav.
  const openMicrosoft = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    window.open('https://pimspanel.dev', '_blank', 'noopener');
  };

  // Kick off an inventory pull for BOTH buckets (same as the per-kind "Run pull"
  // in the Inventory Pull view). Each kind is independent: one already-running
  // run does not block the other. Reports a compact per-kind result.
  const runBothPulls = useCallback(async () => {
    setPulling(true);
    setPullMsg(null);
    const [serkenRegular, msResult] = await Promise.all([
      Promise.all(
        (['serken', 'regular'] as const).map(async (kind) => {
          const label = kind === 'serken' ? 'Serken' : 'Regular';
          try {
            const res = await fetch('/api/inventory-runs', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ kind }),
            });
            if (res.ok) return `${label} started`;
            if (res.status === 409) return `${label} already running`;
            return `${label} failed`;
          } catch {
            return `${label} network error`;
          }
        }),
      ),
      // Microsoft is a synchronous refresh (~4s): it stores + returns the fresh
      // snapshot, which we apply to state so the card updates immediately.
      (async () => {
        try {
          const res = await fetch('/api/inventory-panels/microsoft-refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
          if (res.ok) {
            const body = await res.json();
            if (body?.data) setMs(body.data as MicrosoftSnapshot);
            return 'Microsoft refreshed';
          }
          return 'Microsoft refresh failed';
        } catch {
          return 'Microsoft refresh failed';
        }
      })(),
    ]);
    setPullMsg([...serkenRegular, msResult].join('  ·  '));
    setPulling(false);
  }, []);

  return (
    <div
      className="dash-inv"
      role="button"
      tabIndex={0}
      onClick={goToPage}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToPage();
        }
      }}
    >
      <div className="dash-inv-head">
        <div>
          <h2 className="dash-section-title">Inventory</h2>
          <p className="dash-section-sub">Serken &amp; Regular panels and available licenses</p>
        </div>
        <div className="dash-inv-actions">
          <button
            type="button"
            className="dash-inv-pull-btn"
            disabled={pulling}
            onClick={(e) => {
              e.stopPropagation();
              void runBothPulls();
            }}
          >
            {pulling ? 'Starting...' : 'Run pull (all)'}
          </button>
          <span className="dash-view-all">View all&nbsp;&rsaquo;</span>
        </div>
      </div>
      {pullMsg && <div className="dash-inv-pull-msg">{pullMsg}</div>}

      {!loaded ? (
        <div className="dash-inv-loading">Loading inventory...</div>
      ) : (
        <div className="dash-inv-grid">
          {/* Row 1: supporting panel cards (Serken, Regular live; Microsoft snapshot) */}
          {INVENTORY_BUCKETS.map(({ kind, label, accent }) => {
            const b = summary?.[kind];
            return (
              <div key={`p-${kind}`} className="dash-card">
                <div className={`dash-card-icon dash-card-icon--${accent}`}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="6" rx="1.5" />
                    <rect x="3" y="14" width="18" height="6" rx="1.5" />
                    <line x1="7" y1="7" x2="7.01" y2="7" />
                    <line x1="7" y1="17" x2="7.01" y2="17" />
                  </svg>
                </div>
                <div className="dash-card-value">{fmtNum(b?.count)}</div>
                <div className="dash-card-label">{label} panels</div>
                <div className="dash-card-foot">
                  <span className="dash-card-foot-num">{fmtNum(b?.capacity)}</span> capacity
                </div>
              </div>
            );
          })}
          {/* Microsoft panel card (3rd column, opens pimspanel.dev in a new tab) */}
          <div
            key="p-microsoft"
            className="dash-card dash-card--link"
            role="button"
            tabIndex={0}
            onClick={openMicrosoft}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                window.open('https://pimspanel.dev', '_blank', 'noopener');
              }
            }}
          >
            <div className="dash-card-icon dash-card-icon--blue">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
              </svg>
            </div>
            <div className="dash-card-value">{fmtNum(ms?.admin_panels)}</div>
            <div className="dash-card-label">Microsoft panels</div>
            <div className="dash-card-foot">
              <span className="dash-card-foot-num">{fmtNum(ms?.capacity)}</span> capacity
            </div>
          </div>

          {/* Row 2: highlighted free-license hero cards */}
          {INVENTORY_BUCKETS.map(({ kind, label, accent }) => {
            const b = summary?.[kind];
            const free = b?.free ?? null;
            const cap = b?.capacity ?? 0;
            const pct = free !== null && cap > 0 ? Math.min(100, Math.round((free / cap) * 100)) : 0;
            return (
              <div key={`f-${kind}`} className={`dash-hero dash-hero--${accent}`}>
                <span className="dash-hero-deco" aria-hidden />
                <div className="dash-hero-label">{label} free licenses</div>
                <div className="dash-hero-value">{fmtNum(free)}</div>
                <div className="dash-hero-bar">
                  <span style={{ width: `${pct}%` }} />
                </div>
                <div className="dash-hero-foot">
                  {free !== null ? `${pct}% of ${fmtNum(cap)} capacity free` : 'no completed pull yet'}
                </div>
              </div>
            );
          })}
          {/* Microsoft free-license hero (3rd column). MS is a cached snapshot, so it
              carries an "updated ago" indicator and opens pimspanel.dev in a new tab. */}
          {(() => {
            const free = ms?.free ?? null;
            const cap = ms?.capacity ?? 0;
            const loadedMs = free !== null && ms?.admin_panels !== null && ms?.admin_panels !== undefined;
            const pct = loadedMs && cap > 0 ? Math.min(100, Math.round((free / cap) * 100)) : 0;
            const updated = ms?.updated_at ?? null;
            return (
              <div
                key="f-microsoft"
                className="dash-hero dash-hero--blue dash-hero--link"
                role="button"
                tabIndex={0}
                onClick={openMicrosoft}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open('https://pimspanel.dev', '_blank', 'noopener');
                  }
                }}
              >
                <span className="dash-hero-deco" aria-hidden />
                <div className="dash-hero-label">Microsoft free licenses</div>
                <div className="dash-hero-value">{loadedMs ? fmtNum(free) : '-'}</div>
                <div className="dash-hero-bar">
                  <span style={{ width: `${pct}%` }} />
                </div>
                <div className="dash-hero-foot">
                  {loadedMs ? `${pct}% of ${fmtNum(cap)} capacity free` : 'run pull to load'}
                </div>
                <div className="dash-hero-updated">
                  {updated && loadedMs ? `updated ${formatAgoDM(updated)}` : 'not yet loaded'}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

interface MonitoringSummaryItem {
  kind: 'gmail' | 'health';
  lastRunAt: string | null;
  nextRunAt: string | null;
  healthDot: 'green' | 'yellow' | 'red';
  healthReason: string;
  activeFindings: number;
  inFlight: boolean;
}

const MONITORING_CARDS: ReadonlyArray<{
  kind: 'gmail' | 'health';
  label: string;
  tab: Tab;
}> = [
  { kind: 'gmail', label: 'Gmail Regular', tab: 'monitoring-gmail' },
  { kind: 'health', label: 'Admin Panel Health', tab: 'monitoring-health' },
];

function MonitoringWidget({ onNavigate }: { onNavigate: (tab: Tab) => void }): JSX.Element {
  const [summary, setSummary] = useState<{ gmail: MonitoringSummaryItem; health: MonitoringSummaryItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/monitoring/summary').then((res) => res.json());
      if (!r.success) throw new Error(r.error?.message ?? 'summary failed');
      setSummary(r.data);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  async function runAll() {
    setRunning(true);
    setError(null);
    try {
      const calls = (['gmail', 'health'] as const).map((kind) =>
        fetch('/api/monitoring/runs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kind }),
        }).then((res) => res.json()),
      );
      const results = await Promise.all(calls);
      const errs = results.filter((r) => !r.success);
      if (errs.length > 0) {
        setError(errs.map((r) => r.error?.message).join('; '));
      }
      await refresh();
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="dash-inv">
      <div className="dash-inv-head">
        <div>
          <h2 className="dash-section-title">Monitoring</h2>
          <p className="dash-section-sub">Gmail and admin-panel health scans</p>
        </div>
        <div className="dash-inv-actions">
          <button
            type="button"
            className="dash-inv-pull-btn"
            onClick={() => void runAll()}
            disabled={running}
          >
            {running ? 'Running...' : 'Run all scans'}
          </button>
        </div>
      </div>
      {error && <div className="dash-inv-pull-msg dash-inv-pull-msg--err">{error}</div>}
      {!summary ? (
        <div className="dash-inv-loading">Loading monitoring...</div>
      ) : (
        <div className="dash-card-row">
          {MONITORING_CARDS.map(({ kind, label, tab }) => {
            const s = summary[kind];
            const flagged = s.activeFindings > 0;
            const go = () => onNavigate(tab);
            return (
              <div
                key={kind}
                className="dash-card dash-card--link"
                role="button"
                tabIndex={0}
                onClick={go}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    go();
                  }
                }}
              >
                <div className="dash-card-top">
                  <span className={`dash-health-dot dash-health-dot--${s.healthDot}`} title={s.healthReason} />
                  <span className="dash-card-name">{label}</span>
                </div>
                <div className={`dash-card-value ${flagged ? 'dash-card-value--alert' : 'dash-card-value--ok'}`}>
                  {s.activeFindings}
                </div>
                <div className="dash-card-label">{s.activeFindings === 1 ? 'active flag' : 'active flags'}</div>
                <div className="dash-card-foot dash-card-foot--mono">
                  last {formatAgoDM(s.lastRunAt)}
                  {s.nextRunAt && ` · next ${formatInDM(s.nextRunAt)}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type AutoRemovalInfra = 'GOOGLE' | 'MICROSOFT';

interface AutoRemovalConfig {
  enabled: boolean;
  max_domains: number;
  run_hour: number;
  run_minute: number;
  timezone: string;
}

const AUTO_INFRAS: ReadonlyArray<AutoRemovalInfra> = ['GOOGLE', 'MICROSOFT'];

/** "1:00 PM" style label (no leading zero on the hour, matching the dashboard's compact style). */
function timeLabel12h(hour: number, minute: number): string {
  const h = Math.max(0, Math.min(23, hour));
  const m = Math.max(0, Math.min(59, minute));
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function infraBadgeClass(infra: AutoRemovalInfra): string {
  return infra === 'GOOGLE' ? 'cln-badge--honey' : 'cln-badge--queued';
}

function AutoRemovalWidget({ onNavigate }: { onNavigate: (tab: Tab) => void }): JSX.Element {
  const [configs, setConfigs] = useState<Record<AutoRemovalInfra, AutoRemovalConfig | null>>({
    GOOGLE: null,
    MICROSOFT: null,
  });
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [google, microsoft] = await Promise.all([
        fetch('/api/cleanup/auto-removal/config?infra=GOOGLE').then((r) => r.json()),
        fetch('/api/cleanup/auto-removal/config?infra=MICROSOFT').then((r) => r.json()),
      ]);
      setConfigs({ GOOGLE: google.data ?? null, MICROSOFT: microsoft.data ?? null });
    } catch {
      /* keep last good state */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!loaded) {
    return <div className="dash-inv-loading">Loading auto removals...</div>;
  }

  const goToPage = () => onNavigate('auto-removals');

  return (
    <div
      className="dash-inv"
      role="button"
      tabIndex={0}
      onClick={goToPage}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToPage();
        }
      }}
    >
      <div className="dash-inv-head">
        <div>
          <h2 className="dash-section-title">Auto Removals</h2>
          <p className="dash-section-sub">Autonomous Airtable-driven domain removal</p>
        </div>
        <span className="dash-view-all">View all&nbsp;&rsaquo;</span>
      </div>

      <div className="dash-card-row">
        {AUTO_INFRAS.map((infra) => {
          const cfg = configs[infra];
          const enabled = cfg?.enabled ?? false;
          return (
            <div
              key={infra}
              className={`dash-card dash-auto-card ${enabled ? 'dash-auto-card--on' : 'dash-auto-card--off'}`}
            >
              <div className="dash-card-top">
                <span className={`cln-badge ${infraBadgeClass(infra)}`}>{infra}</span>
                {cfg && (
                  <span className={`dash-auto-status dash-auto-status--${enabled ? 'on' : 'off'}`}>
                    <span className="dash-auto-status-dot" />
                    {enabled ? 'Enabled' : 'Disabled'}
                  </span>
                )}
              </div>
              {cfg ? (
                <>
                  <div className="dash-card-value dash-auto-time-value">
                    {timeLabel12h(cfg.run_hour, cfg.run_minute)}
                  </div>
                  <div className="dash-card-label">Daily · {cfg.timezone}</div>
                  <div className="dash-card-foot">
                    <span className="dash-card-foot-num">{cfg.max_domains.toLocaleString('en-US')}</span> domain cap
                  </div>
                </>
              ) : (
                <div className="dash-card-label" style={{ marginTop: 14 }}>
                  config unavailable
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatAgoDM(iso: string | null): string {
  if (!iso) return '-';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  return `${(m / 60).toFixed(1)}h ago`;
}
function formatInDM(iso: string | null): string {
  if (!iso) return '-';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return 'overdue';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `in ${m} min`;
  return `in ${(m / 60).toFixed(1)}h`;
}
